<template>
    <section>
        <BlocksCLHero v-bind="computedComponent">
            <template #buttons>
                <button
                    class="ds-btn ds-btn--primary ds-btn--large"
                    @click="startOrder"
                >{{ $i18n.t('buttons.order') }}</button>
            </template>
        </BlocksCLHero>
    </section>
    <Teleport v-if="modal.name === 'munkkimellakka-form'" :to="modal.modalTarget">
        <div class="ds-modal-content size-small">
            <div class="header">
                <h5 class="h6 uppercase">{{ stepTitle }}</h5>
                <button
                    @click="modal.unsetNameAndClose(); reset()"
                >
                    <DSIcon
                        code="close"
                        size="m"
                    />
                </button>
            </div>
            <div class="body">
                <BlocksMunkkimellakkaSummary
                    :step="step"
                    :station
                    :date-string
                    :selected-products
                />
                <BlocksMunkkimellakkaStep1 v-if="step === 1" :stations="data" :selected="station" />
                <BlocksMunkkimellakkaStep2 v-else-if="step === 2" />
                <BlocksMunkkimellakkaStep3 v-else-if="step === 3" />
                <BlocksMunkkimellakkaStep4 v-else-if="step === 4" />
                <BlocksMunkkimellakkaStep5 v-else-if="step === 5" :station />
                <BlocksMunkkimellakkaCancel v-else />
            </div>
            <div class="footer">
                <template v-if="step < 5">
                    <button
                        v-if="step > 1"
                        class="ds-btn ds-btn--large ds-btn--secondary"
                        @click="prevStep()"
                    >
                        {{ $i18n.t('buttons.back') }}
                    </button>
                    <button
                        class="ds-btn ds-btn--large ds-btn--secondary"
                        @click="cancelOrder"
                    >
                        {{ $i18n.t('buttons.cancel') }}
                    </button>
                </template>
                <button
                    class="ds-btn ds-btn--large ds-btn--primary"
                    :disabled="!validStep"
                    @click="nextStep()"
                >
                    {{ nextButtonText }}
                </button>
            </div>
        </div>
    </Teleport>
</template>
<script setup>
import { ref, computed, provide, useI18n, useNuxtApp, useFormData, useAnalytics } from '#imports';
import { DateTime } from 'luxon';
import { useModalStore } from '~dsc/stores/modal';
import { useNotificationsStore } from '~dsc/stores/notifications';

const gtm = useAnalytics();
const { $i18n, $cms, $paths } = useNuxtApp();
const modal = useModalStore();

defineOptions({
    name: 'MunkkimellakkaForm',
    inheritAttrs: false,
});

const props = defineProps({
    data: {
        type: Object,
        required: false,
        default: () => ({}),
    },
});

const formData = useFormData([
    { key: 'station', value: null, validators: ['notEmpty'] },
    { key: 'time', value: null, validators: ['notEmpty'] },
    { key: 'comments', value: null },
    { key: 'contact', value: null, validators: ['notEmpty'] },
    { key: 'phone', value: null, validators: ['notEmpty', 'phone'] },
    { key: 'email', value: null, validators: ['notEmpty', 'email'] },
    { key: 'products', value: [0, 0, 0, 0] },
    { key: 'terms', value: null, validators: ['notEmpty'] },
    { key: 'emailVerified', value: null, validators: ['notEmpty'] },
]);

const step = ref(1);
const steps = ref(4);
const date = ref(DateTime.fromISO('2025-04-30T12:00:00'));
const dateString = ref(date.value.toFormat('dd.MM.yyyy'));
const previousStep = ref(null);
const products = ref([
    { product: '6 kpl Itse tehtyä sokeroitua munkkia', pcs: 6, price: 8.00 },
    { product: '1 kpl Itse tehty sokeroitu munkki', pcs: 1, price: 1.70 },
    { product: '6 kpl Kuorrutettua minimunkkia', pcs: 6, price: 8.00 },
    { product: '1 kpl Kuorrutettu minimunkki', pcs: 1, price: 1.70 },
]);
const loading = ref(false);

provide('order', formData);
provide('products', products);
provide('date', dateString);

const station = computed(() => {
    const station = props.data.find(station => station.id === formData.data.value.station);
    if (station) return station;

    return null;
});

const nextButtonText = computed(() => {
    if (step.value === 5) {
        return $i18n.t('buttons.close');
    } else if (step.value === 4) {
        return $i18n.t('buttons.send');
    } else if (!step.value) {
        return $i18n.t('buttons.continue');
    }

    return $i18n.t('buttons.next');
});

const computedComponent = computed(() => ({
    cards: [
        {
            contentType: 'card',
            image: {
                ext: 'jpg',
                width: 3600,
                height: 2700,
                generic_url: 'https://media.ffycdn.net/eu/st1/ZoPG3KtUHAmRUDGFDZkm.jpg?width={width}',
                fill: 'cover',
            },
            alt: 'Munkit ovat irti!',
            byline: 'Munkkimellakka',
            title: 'Tilaa vappumunkit ennakkoon',
            text: {
                nodeType: 'document',
                content: [
                        'Munkit ovat irti!',
                        'MUNKKIMELLAKKA valtaa jälleen HelmiSimpukat – valmistaudu vapun juhlintaan ja ennakkotilaa meillä itse tehdyt munkit napin painalluksella. Voit tilata haluamasi määrän munkkeja valikoiduista HelmiSimpukoista.',
                    ].map(text => ({
                        nodeType: 'paragraph',
                        content: [{ nodeType: 'text', value: text, marks: [] }],
                    }),
                ),
            },
        },
    ],
    display: {
        type: 'horizontal',
        alignment: 'left',
        component: 'hero',
        background: 'secondary',
    },
}));

const stepTitle = computed(() => {
    if (step.value !== 0 && step.value <= steps.value) {
        return `${$i18n.t('title')} ${date.value.toFormat('dd.MM.yyyy')}${step.value > 0 ? `, ${$i18n.t('step')} ${step.value}/${steps.value}` : ''}`;
    }

    return $i18n.t('title');
});

const total = computed(() => ({
    amount: formData.data.value.products.reduce((acc, val) => acc + val),
    pcs: products.value.map((item, key) => item.pcs * formData.data.value.products[key]).reduce((acc, val) => acc + val),
    price: products.value.map((item, key) => item.price * formData.data.value.products[key]).reduce((acc, val) => acc + val),
}));

provide('total', total);

const validStep = computed(() => {
    if (step.value === null) {
        return true;
    } else if (step.value === 1) {
        return formData.isValid('station');
    } else if (step.value === 2) {
        return formData.isValid('time') && total.value.pcs > 0;
    } else if (step.value === 3) {
        return formData.isValid('contact') && formData.isValid('phone') && formData.isValid('email');
    } else if (step.value === 4) {
        return formData.isValid('emailVerified') && formData.isValid('terms');
    } else if (step.value === 5) {
        return true;
    }

    return false;
});

const selectedProducts = computed(() => products.value.map((item, key) => {
    const amount = formData.data.value.products[key];
    const unit = item.pcs > 1 ? 'package' : 'pcs';
    const price = amount * item.price;

    if (amount > 0) return `${item.product} x ${$i18n.t(unit, amount)}, ${$cms.formatPrice(price)}`;

    return null;
}).filter(v => v));

function startOrder() {
    step.value = 1;
    modal.setNameAndOpen('munkkimellakka-form');
}

function cancelOrder() {
    if (step.value === null) {
        modal.unsetNameAndClose();
        reset();

        return;
    }

    previousStep.value = step.value;
    step.value = null;
}

function prevStep() {
    step.value = step.value - 1;
}

async function nextStep() {
    if (!step.value) {
        step.value = previousStep.value;
        previousStep.value = null;

        return;
    }

    if (step.value === 4) {
        await sendOrder();

        return;
    } else if (step.value === 5) {
        modal.unsetNameAndClose();
        reset();

        return;
    }

    step.value = step.value + 1;
}

function reset() {
    previousStep.value = null;
    step.value = 1;
    formData.reset();
}

async function sendOrder() {
    loading.value = true;
    const { email, phone, time, contact: name, additionalInfo } = formData.data.value;
    const body = {
        stationId: station.value.id,
        customer: {
            name,
            email,
            phone,
            contactMethod: 'email',
        },
        order: {
            products: selectedProducts.value,
            type: 'munkkimellakka',
            pickupTime: `${dateString.value} ${time}`,
        },
    };
    if (additionalInfo) body.order.details = additionalInfo;

    const gtmData = {
        event: 'munkkimellakka_form_submit',
        form_id: '7pKtpQzd9LQiBPt9qS72NM',
        form_description: 'Munkkimellakka',
        form_value: total.value.price ?? '0',
        form_station_id: station.value.id ?? '',
        form_station_name: station.value.name ?? '',
    };

    try {
        await $fetch(`/proxy/${$paths.getEndpoint('groupOrder')}`, {
            method: 'post',
            body,
        });
        gtm.gtm().push(gtmData);
        step.value = step.value + 1;
    } catch (e) {
        useNotificationsStore().addNotification({
            type: 'error',
            message: $i18n.t('orderFailed'),
        });
    }

    loading.value = false;
}

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            buttons: {
                order: 'Tilaa tästä',
                cancel: 'Keskeytä',
                next: 'Seuraava',
                continue: 'Jatka',
                back: 'Takaisin',
                send: 'Lähetä',
                close: 'Sulje',
            },
            step: 'Vaihe',
            title: 'Munkkimellakka',
            paymentOnLocation: 'Maksu tapahtuu paikan päällä.',
            orderIsConfirmed: 'Tilaus on vahvistettu, kun saat vahvistusviestin sähköpostiin. Asema ottaa yhteyttä jos tilaukseen tarvitaan lisätietoja.',
            package: '{n} paketti | {n} pakettia',
            pcs: '{n} kpl',
            orderFailed: 'Tilauksen lähettäminen ei onnistunut, kokeile myöhemmin uudelleen tai ole yhteydessä asemaan.',
        },
    },
});
</script>
<style scoped>
.ds-modal-content .header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.ds-modal-content .header .h6 {
  margin: 0;
}
.ds-modal-content .header button {
  display: inline-flex;
}
.ds-modal-content .header, .ds-modal-content .footer {
  padding: 1.5rem;
}
.ds-modal-content .footer {
  display: flex;
  justify-content: center;
}
.ds-modal-content .footer button:not(:last-child) {
  margin-right: 1rem;
}
@media (max-width: 1199px) {
  .ds-modal-content .footer button {
    padding: 0.375rem 0.5rem;
  }
}
.ds-modal-content .body {
  padding: 0 1.5rem;
}
</style>
